import { useEffect, useState } from "react";
import { Loader2, HandHeart, Banknote, Package, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorMessage } from "../../lib/api";
import { nok } from "../../lib/basar";
import { normalizePhone } from "./BuyPanel";
import { useLang } from "../../context/LanguageContext";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const METHODS = [
  { value: "cash", label: "Kontant" },
  { value: "vipps_manual", label: "Vipps ved døren" },
  { value: "bank", label: "Bankoverføring" },
  { value: "other", label: "Annen betaling" },
];
const EMPTY = { kind: "item", name: "", description: "", value: "", count: "1", donor_name: "", donor_kind: "private", donor_phone: "", donor_email: "", message: "", note: "", payment_method: "cash", collected: false, send_receipt: true };

const fromGift = (g) => ({
  kind: g.kind, name: g.kind === "item" ? g.name || "" : "", description: g.description || "", value: String(g.value ?? ""), count: String(g.count ?? 1), donor_name: g.donor_name || "",
  donor_kind: g.donor_kind || "private", donor_phone: g.donor_phone || "", donor_email: g.donor_email || "", message: g.message || "", note: g.note || "", payment_method: g.payment_method || "cash",
  collected: !!g.collected_at, send_receipt: false,
});

/**
 * Manuell registrering / redigering av ei gåve i gåveregisteret (arrangør). Tinggåve (namn, verdi, tal like) eller pengegåve motteken
 * utanom Right in (beløp + betalingsmåte). Gåva blir premie automatisk; endringar blir spegla til premien før publisert trekning.
 * `gift` = null → ny. `onSaved(gift, basar)`.
 */
export const GiftFormDialog = ({ basar, open, onOpenChange, gift = null, onSaved, emailEnabled = false }) => {
  const { t } = useLang();
  const editing = !!gift;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const published = !!basar?.draw_published_at;

  useEffect(() => {
    if (open) setForm(gift ? fromGift(gift) : EMPTY);
  }, [open, gift]);

  const value = parseInt(form.value, 10) || 0;
  const count = Math.max(1, parseInt(form.count, 10) || 1);
  const money = form.kind === "money";
  const valid = (money || form.name.trim().length >= 2) && value >= 1 && (!form.donor_phone.trim() || !!normalizePhone(form.donor_phone)) && (!form.donor_email.trim() || EMAIL_RE.test(form.donor_email.trim()));

  const save = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error(money ? t("Oppgi beløpet i kroner") : t("Gaven må ha navn og verdi"));
      return;
    }
    setSaving(true);
    try {
      const donor = { donor_name: form.donor_name.trim(), donor_kind: form.donor_kind, donor_phone: form.donor_phone.trim() ? normalizePhone(form.donor_phone) : "", donor_email: form.donor_email.trim(), message: form.message.trim(), note: form.note.trim() };
      if (editing) {
        const patch = { ...donor };
        if (!published) {
          patch.value = value;
          if (!money) {
            patch.name = form.name.trim();
            patch.count = count;
            patch.description = form.description.trim();
          }
        }
        if (money) patch.payment_method = form.payment_method;
        const { data } = await api.patch(`/my/basar/${basar.id}/gifts/${gift.id}`, patch);
        toast.success(t("Gaven er oppdatert"));
        onSaved?.(data.gift, data.basar);
      } else {
        const payload = { kind: form.kind, name: money ? "" : form.name.trim(), description: form.description.trim(), value, count: money ? 1 : count, ...donor, payment_method: form.payment_method, collected: money ? true : form.collected, send_receipt: form.send_receipt };
        const { data } = await api.post(`/my/basar/${basar.id}/gifts`, payload);
        toast.success(data.message || t("Gaven er registrert"));
        onSaved?.(data.gift, data.basar);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="basar-gift-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HandHeart className="w-5 h-5 text-[hsl(var(--warning))]" /> {editing ? t("Rediger gave") : t("Registrer gave")}</DialogTitle>
          <DialogDescription>{editing ? t("Endringer i navn, verdi og antall oppdaterer også premien (før trekningen er publisert).") : t("Gaver dere har fått utenom nettsiden – gjenstander eller penger. Gaven legges inn som premie med en gang.")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          {!editing && (
            <RadioGroup value={form.kind} onValueChange={(v) => set("kind", v)} className="grid grid-cols-2 gap-2" data-testid="basar-gift-form-kind-group">
              <label className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${form.kind === "item" ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]" : "border-border hover:bg-accent"}`}>
                <RadioGroupItem value="item" id="gf-kind-item" data-testid="basar-gift-form-kind-item" /> <span className="text-sm font-medium inline-flex items-center gap-1.5"><Package className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Tinggave")}</span>
              </label>
              <label className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${form.kind === "money" ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]" : "border-border hover:bg-accent"}`}>
                <RadioGroupItem value="money" id="gf-kind-money" data-testid="basar-gift-form-kind-money" /> <span className="text-sm font-medium inline-flex items-center gap-1.5"><Banknote className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Pengegave")}</span>
              </label>
            </RadioGroup>
          )}

          {money ? (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="basar-gift-form-money">
              <div className="space-y-1.5">
                <Label htmlFor="gf-amount">{t("Beløp (kr)")}</Label>
                <Input id="gf-amount" type="number" inputMode="numeric" min={1} value={form.value} onChange={(e) => set("value", e.target.value.replace(/\D/g, "").slice(0, 7))} required disabled={published} data-testid="basar-gift-form-amount-input" />
                <p className="text-xs text-muted-foreground">{value >= 1 ? t("Premie: «Pengepremie {amount}»", { amount: nok(value) }) : t("Blir premien «Pengepremie …»")}</p>
              </div>
              <div className="space-y-1.5">
                <Label>{t("Mottatt som")}</Label>
                <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger data-testid="basar-gift-form-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`basar-gift-form-method-${m.value}`}>{t(m.label)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="basar-gift-form-item">
              <div className="space-y-1.5">
                <Label htmlFor="gf-name">{t("Hva er gaven?")}</Label>
                <Input id="gf-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} placeholder={t("F.eks. Rosebuketter fra Spar Sæbøvik")} required disabled={published} data-testid="basar-gift-form-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="gf-value">{t("Verdi per stk (kr)")}</Label>
                  <Input id="gf-value" type="number" inputMode="numeric" min={1} value={form.value} onChange={(e) => set("value", e.target.value.replace(/\D/g, "").slice(0, 7))} required disabled={published} data-testid="basar-gift-form-value-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="gf-count">{t("Antall like")}</Label>
                  <Input id="gf-count" type="number" inputMode="numeric" min={1} max={100} value={form.count} onChange={(e) => set("count", e.target.value.replace(/\D/g, "").slice(0, 3))} disabled={published} data-testid="basar-gift-form-count-input" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t("Flere like gaver = like mange separate gevinster (f.eks. 3 rosebuketter → 3 vinnere).")}</p>
              <div className="space-y-1.5">
                <Label htmlFor="gf-desc">{t("Beskrivelse (valgfritt)")}</Label>
                <Textarea id="gf-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} maxLength={600} disabled={published} data-testid="basar-gift-form-description-input" />
              </div>
            </div>
          )}

          <div className="space-y-3 border-t border-border pt-4" data-testid="basar-gift-form-donor">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm">{t("Giver (vises aldri offentlig)")}</Label>
              <RadioGroup value={form.donor_kind} onValueChange={(v) => set("donor_kind", v)} className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="private" id="gf-donor-private" data-testid="basar-gift-form-donor-private" /> {t("Privatperson")}</label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="company" id="gf-donor-company" data-testid="basar-gift-form-donor-company" /> {t("Bedrift")}</label>
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gf-donor-name">{t("Navn (valgfritt)")}</Label>
              <Input id="gf-donor-name" value={form.donor_name} onChange={(e) => set("donor_name", e.target.value)} maxLength={120} placeholder={t("Tomt = ukjent/anonym giver")} data-testid="basar-gift-form-donor-name-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="gf-donor-phone">{t("Mobilnummer")}</Label>
                <Input id="gf-donor-phone" type="tel" inputMode="tel" value={form.donor_phone} onChange={(e) => set("donor_phone", e.target.value)} maxLength={20} placeholder="912 34 567" data-testid="basar-gift-form-donor-phone-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gf-donor-email">{t("E-post")}</Label>
                <Input id="gf-donor-email" type="email" value={form.donor_email} onChange={(e) => set("donor_email", e.target.value)} maxLength={200} data-testid="basar-gift-form-donor-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gf-note">{t("Intern merknad (valgfritt)")}</Label>
              <Textarea id="gf-note" value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} maxLength={600} placeholder={t("F.eks. hentes fredag etter kl. 15")} data-testid="basar-gift-form-note-input" />
            </div>
          </div>

          {!editing && (
            <div className="space-y-2">
              {!money && (
                <label className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer">
                  <Switch checked={form.collected} onCheckedChange={(v) => set("collected", v)} data-testid="basar-gift-form-collected-switch" />
                  <span className="text-sm">{t("Gaven er allerede mottatt")}</span>
                </label>
              )}
              {emailEnabled && form.donor_email.trim() && (
                <label className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer">
                  <Switch checked={form.send_receipt} onCheckedChange={(v) => set("send_receipt", v)} data-testid="basar-gift-form-receipt-switch" />
                  <span className="text-sm inline-flex items-center gap-1.5"><Mail className="w-4 h-4 text-muted-foreground" /> {t("Send takk til giveren på e-post")}</span>
                </label>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="basar-gift-form-cancel">{t("Avbryt")}</Button>
            <Button type="submit" disabled={saving || !valid} data-testid="basar-gift-form-save">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <HandHeart className="w-4 h-4 mr-1.5" />} {editing ? t("Lagre") : t("Registrer gave")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
